import { z } from "zod";

/*
 * Desktop columns are rail | panel | list | thread (LEA-501). When saved widths do not fit, the
 * panel narrows, then the list, then the panel folds, preserving the thread's minimum. Email layouts
 * typically need 600–640 px.
 */
export const LIST = { default: 400, min: 260, max: 480 } as const;
export const THREAD_MIN = 480;
/**
 * Fixed space around the thread on desktop: the detail's 28 px padding each side, and no column gap,
 * since the thread is the detail's only column. `tests/browser/pane-fit.pw.ts` measures the drawn
 * page against it.
 */
export const DETAIL_OVERHEAD = 56;
/** Arrow-key step on a focused divider. */
export const STEP = 16;

/**
 * The width at and below which the phone layout takes over: the rail gives way to the top bar and
 * its tray. One home for the number, because the stylesheet, the tray's media query and the rail's
 * width arithmetic all depend on it agreeing.
 */
export const PHONE_MAX_WIDTH = 700;

/** The app rail: the sections as icons, never collapsed or widened (LEA-501). */
export const APP_RAIL = 64;
/**
 * The section panel beside the rail (the inbox's rows, the settings groups). It narrows to `min`,
 * the narrowest at which "Meeting requests" and a three-digit count still read whole (DESIGN.md),
 * before the list gives anything.
 */
export const PANEL = { default: 224, min: 200 } as const;
/**
 * The narrowest window that seats the panel: the list at its minimum and the thread at its own. The
 * same for every saved list width, since the list narrows to its minimum before the panel folds.
 */
export const PANEL_FLOOR = APP_RAIL + PANEL.min + LIST.min + DETAIL_OVERHEAD + THREAD_MIN;

/* The panel cannot be hidden by choice (LEA-501), so the only saved width is the list's. */
export type PaneLayout = { list: number };
export const defaultPaneLayout: PaneLayout = { list: LIST.default };

/** The drawn widths: the rail (0 on a phone), the panel (0 where folded or absent) and the list. */
export type FittedPanes = { rail: number; panel: number; list: number };

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(Math.round(value), min), max);

/*
 * Old layouts carried the rail's width (and before that the Work column's and a hidden list); all
 * are read and dropped, so a browser's stored layout keeps its list width.
 */
const StoredSchema = z.object({ list: z.number().finite() });

/** Browser storage is untrusted: anything unreadable falls back to the default, the width clamps. */
export function parsePaneLayout(raw: string | null): PaneLayout {
  if (!raw) return defaultPaneLayout;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return defaultPaneLayout;
  }
  const parsed = StoredSchema.safeParse(json);
  if (!parsed.success) return defaultPaneLayout;
  return { list: clamp(parsed.data.list, LIST.min, LIST.max) };
}

/** Dragging the list edge: the width clamps to the list's limits and never hides it. */
export function resizeList(layout: PaneLayout, proposed: number): PaneLayout {
  return { ...layout, list: clamp(proposed, LIST.min, LIST.max) };
}

/** The list's width once `owed` pixels must come out of it, never below its minimum. */
const listGiving = (list: number, owed: number): number =>
  list - Math.min(Math.max(owed, 0), list - LIST.min);

/**
 * Fit widths without changing saved preferences. `panel` says whether the page has a section panel;
 * `phone` draws neither rail nor panel. Space is given up in order: the panel narrows to its minimum,
 * then the list to its own, then the panel folds (LEA-501). Below `PANEL_FLOOR` the list keeps 260
 * and the thread takes what is left, as on any window too narrow for both minimums. Fit depends only
 * on the window and the saved width, so opening a conversation moves no column.
 */
export function fitPaneLayout(
  layout: PaneLayout,
  windowWidth: number,
  overhead: number,
  page: { readonly phone: boolean; readonly panel: boolean },
): FittedPanes {
  const rail = page.phone ? 0 : APP_RAIL;
  /** How many pixels the thread is short of its minimum beside these widths. */
  const owed = (panel: number, list: number) =>
    rail + panel + list + overhead + THREAD_MIN - windowWidth;
  const folded = { rail, panel: 0, list: listGiving(layout.list, owed(0, layout.list)) };
  if (page.phone || !page.panel) return folded;
  const panel =
    PANEL.default -
    Math.min(Math.max(owed(PANEL.default, layout.list), 0), PANEL.default - PANEL.min);
  const list = listGiving(layout.list, owed(panel, layout.list));
  return owed(panel, list) > 0 ? folded : { rail, panel, list };
}
